import React, { Dispatch } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { ITableColumns, ITag, TemplateTableAction, TemplateTableState } from '../types';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from '../API';
import GenericTable from '../../Components/GenericTable/GenericTable';
import { GenericTableAction } from '../../Components/GenericTable/genericTableReducer';
import TagManagerTableRow from './TagManagerTableRow';
import { headerStyle, ModalType, tagsActionsListItem } from '../config';
import Search from '../../SmartForms/Search';
import GenericDialog from '../../Components/Modals/GenericDialog';
import EmptyFolder from '../../../NewUI/EditJob/Questions/Modals/Icons/EmptyFolder.svg';
import { styles } from '../styles';
import TagManagerActionsPanel from './TagManagerActionsPanel';
import TagMergerModal from './Modals/TagMergerModal';
import { classes } from '../../Job/Applications/styles';
import { ISnackbarInput } from '../../Components/CustomUIElements/StyledSnackbar';
import NewTagModal from './Modals/NewTagModal';

export default function TagManager({
  templatePageState,
  templatePageDispatch
}: {
  templatePageState: TemplateTableState<ITableColumns>;
  templatePageDispatch: Dispatch<TemplateTableAction>;
}) {
  const newTagModalOpen =
    templatePageState.modalsOpen === ModalType.NEW_TAG ||
    templatePageState.templateTableState.actions.isOpen.edit;

  const {
    data,
    isLoading: loadingTags,
    refetch: refetchTags
  } = useQuery({
    queryKey: ['tags', templatePageState.templateTableSettings, templatePageState.searchQuery],
    queryFn: async () =>
      await Api.getTags({
        page: templatePageState.templateTableSettings.page,
        limit: templatePageState.templateTableSettings.limit,
        sort: templatePageState.templateTableSettings.sort,
        sort_order: templatePageState.templateTableSettings.sort_order,
        search: templatePageState.searchQuery
      }),
    onError: () => {
      templatePageDispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error getting tag data`,
          state: 'error'
        }
      });
    }
  });

  const { mutate: deleteTag, isLoading: deletingTag } = useMutation({
    mutationFn: (id: string) => Api.deleteTag(id),
    onSuccess: () => {
      templatePageDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Tag deleted successfully', state: 'success' }
      });
      templatePageDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'delete' });
      refetchTags();
    },
    onError: () => {
      templatePageDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'There was an error deleting the tag', state: 'error' }
      });
    }
  });

  const mergeTagModalOpen =
    templatePageState.modalsOpen === ModalType.MERGE_TAGS ||
    templatePageState.templateTableState.actions.isOpen.merge;
  return (
    <Stack sx={{ rowGap: 2, paddingTop: 5 }}>
      <Box sx={classes.applicationsContainer}>
        <TagManagerActionsPanel {...{ templatePageState, templatePageDispatch }} />
        <Box sx={classes.filtersRow}>
          <Search
            idLabel="tag-manager"
            placeholder="Search tag by name"
            setSearch={(title) =>
              templatePageDispatch({ type: 'SET_SEARCH_QUERY', payload: title })
            }
          />
          <Button
            onClick={() => {
              templatePageDispatch({ type: 'SET_MODALS_OPEN', payload: ModalType.NEW_TAG });
            }}
            sx={{ ...classes.newApplicationButton, marginBottom: 0.5 }}
            id="new-tag-button"
          >
            New Tag
          </Button>
        </Box>
        <GenericTable
          label="tag list"
          items={(data?.res.tags || []).filter((item): item is ITag => 'name' in item)}
          isLoadingItems={loadingTags}
          tableState={templatePageState.templateTableState as unknown as any}
          dispatch={templatePageDispatch as Dispatch<GenericTableAction<ITag>>}
          headerStyle={headerStyle}
          paginationState={templatePageState.templateTableSettings}
          pagination={{
            totalItems: Number(data?.resHead['x-total-count']) || 0,
            totalPages: Number(data?.resHead['x-total-pages']) || 0
          }}
          TableRows={TagManagerTableRow as unknown as any}
          TableActionList={tagsActionsListItem}
          allowMultipleSelection={true}
          titleCellId="name"
          emptyMessageOverride={
            !data?.res.tags.length &&
            !templatePageState.searchQuery && (
              <Stack sx={styles.questionLibraryModalEmptyStateContainer}>
                <Stack component="img" src={EmptyFolder} sx={{ width: '8rem' }} />
                <Stack sx={styles.questionLibraryModalEmptyStateMessage}>No Tags available</Stack>
              </Stack>
            )
          }
        />
        <GenericDialog
          isDialogOpen={templatePageState.templateTableState.actions.isOpen.delete}
          setDialogOpen={() =>
            templatePageDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'delete' })
          }
          title="Trashing Tag?"
          description={
            <Stack>
              <Stack>Are you sure you want to Trash this Tag?</Stack>
              <Stack>This will also remove the tag from assigned candidates</Stack>
            </Stack>
          }
          buttonCallback={() => {
            const item = templatePageState.templateTableState.actions.selectedItem;
            if (item) {
              deleteTag(item.id.toString());
            }
          }}
          callbackLoading={deletingTag}
          buttonText="Yes, Trash"
          secondaryButtonText="Cancel"
          url=""
        />
        <TagMergerModal
          modalOpen={mergeTagModalOpen}
          onClose={() => {
            templatePageDispatch({ type: 'SET_MODALS_OPEN', payload: null });
          }}
          refetchTags={refetchTags}
          {...{ templatePageState, templatePageDispatch }}
        />
        {newTagModalOpen && (
          <NewTagModal
            modalOpen={newTagModalOpen}
            onClose={() => {
              templatePageDispatch({ type: 'SET_MODALS_OPEN', payload: null });
              templatePageDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'edit' });
            }}
            selectedTag={templatePageState.templateTableState.actions.selectedItem}
            editingTag={templatePageState.templateTableState.actions.isOpen.edit}
            setSnackbar={(payload: ISnackbarInput) =>
              templatePageDispatch({ type: 'SET_SNACKBAR', payload: payload })
            }
          />
        )}
      </Box>
    </Stack>
  );
}
